import { CheckCircle2, Clock, CreditCard, Truck, Package, DollarSign, AlertTriangle, XCircle, Shield, FileText } from 'lucide-react';
import { getTransactionFlowType } from './transactionState';
import { Product as D } from './productTokens';

const COLORS = {
  primary: D.text,
  green: D.success,
  warning: D.warn,
  error: D.error,
  info: D.accent,
  subtext: D.sub,
};

const TIMELINE_STEPS = [
  { 
    key: 'created', 
    label: 'Transaction Created', 
    icon: FileText,
    states: ['CREATED']
  },
  { 
    key: 'confirmed', 
    label: 'Confirmed by Both Parties', 
    icon: CheckCircle2,
    states: ['PENDING_CONFIRMATION', 'AWAITING_PAYMENT']
  },
  {
    key: 'payment',
    label: 'Payment Protected',
    icon: Shield,
    states: ['PAYMENT_SECURED']
  },
  { 
    key: 'delivery', 
    label: 'Delivery in Progress', 
    icon: Truck,
    states: ['DELIVERY_IN_PROGRESS']
  },
  { 
    key: 'delivered', 
    label: 'Delivered', 
    icon: Package,
    states: ['DELIVERED']
  },
  { 
    key: 'completed', 
    label: 'Funds Released', 
    icon: DollarSign,
    states: ['COMPLETED']
  }
];

function getTimelineSteps(transaction) {
  const flowType = getTransactionFlowType(transaction);
  if (flowType === 'delivery') return TIMELINE_STEPS;
  return TIMELINE_STEPS.map((step) => {
    if (step.key === 'delivery') {
      return {
        ...step,
        label: flowType === 'instant' ? 'Release Processing' : 'Release Conditions',
        icon: Shield,
      };
    }
    if (step.key === 'delivered') {
      return {
        ...step,
        label: flowType === 'instant' ? 'Release Conditions Met' : 'Completion Confirmed',
        icon: CheckCircle2,
      };
    }
    return step;
  });
}

function getStepStatus(stepStates, currentState, allTimelineEvents) {
  // Check if any step state matches current state
  const isCurrentStep = stepStates.includes(currentState);
  
  // Check if step is completed (current state is beyond this step)
  const stateOrder = ['CREATED', 'PENDING_CONFIRMATION', 'AWAITING_PAYMENT', 'PAYMENT_SECURED', 'DELIVERY_IN_PROGRESS', 'DELIVERED', 'COMPLETED'];
  const currentIndex = stateOrder.indexOf(currentState);
  const stepHighestIndex = Math.max(...stepStates.map(s => stateOrder.indexOf(s)));
  
  const isCompleted = currentIndex > stepHighestIndex;
  
  return { isCurrentStep, isCompleted };
}

export function TransactionTimeline({ transaction, currentState, timeline = [] }) {
  const steps = getTimelineSteps(transaction);
  // Handle disputed or cancelled states
  const isDisputed = currentState === 'DISPUTED';
  const isCancelled = currentState === 'CANCELLED';
  const isRefunded = currentState === 'REFUNDED';
  const isExpired = currentState === 'EXPIRED';
  
  if (isDisputed || isCancelled || isRefunded || isExpired) {
    return (
      <div className="space-y-4">
        {/* Show completed steps */}
        {steps.map((step, index) => {
          const { isCompleted } = getStepStatus(step.states, currentState, timeline);
          const Icon = step.icon;
          
          // For disputed/cancelled, show what was completed before
          const wasCompleted = timeline?.some(t => 
            step.states.some(s => t.status?.toLowerCase().includes(s.toLowerCase()))
          );
          
          if (!wasCompleted && !isCompleted) return null;
          
          return (
            <div key={step.key} className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${COLORS.green}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: COLORS.green }} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm" style={{ color: COLORS.primary }}>{step.label}</p>
              </div>
              <CheckCircle2 className="w-5 h-5" style={{ color: COLORS.green }} />
            </div>
          );
        })}
        
        {/* Show special state */}
        <div className="flex items-center gap-4 p-3 rounded-lg" style={{ backgroundColor: `${isDisputed ? COLORS.warning : isExpired ? COLORS.subtext : COLORS.error}15` }}>
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${isDisputed ? COLORS.warning : isExpired ? COLORS.subtext : COLORS.error}30` }}
          >
            {isDisputed ? (
              <AlertTriangle className="w-5 h-5" style={{ color: COLORS.warning }} />
            ) : isExpired ? (
              <Clock className="w-5 h-5" style={{ color: COLORS.subtext }} />
            ) : (
              <XCircle className="w-5 h-5" style={{ color: COLORS.error }} />
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm" style={{ color: isDisputed ? COLORS.warning : isExpired ? COLORS.subtext : COLORS.error }}>
              {isDisputed ? 'Under Review' : isExpired ? 'Transaction expired' : isCancelled ? 'Cancelled' : 'Refunded'}
            </p>
            <p className="text-xs" style={{ color: COLORS.subtext }}>
              {isDisputed ? 'TrustTrade is reviewing this dispute' : isExpired ? 'Transaction expired due to no payment' : 'This transaction has ended'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      {steps.map((step, index) => {
        const { isCurrentStep, isCompleted } = getStepStatus(step.states, currentState, timeline);
        const Icon = step.icon;
        const isLast = index === TIMELINE_STEPS.length - 1;
        
        let stepColor = COLORS.subtext;
        let bgColor = `${COLORS.subtext}15`;
        let lineColor = COLORS.subtext;
        
        if (isCompleted) {
          stepColor = COLORS.green;
          bgColor = `${COLORS.green}15`;
          lineColor = COLORS.green;
        } else if (isCurrentStep) {
          stepColor = COLORS.info;
          bgColor = `${COLORS.info}15`;
        }
        
        return (
          <div key={step.key} className="flex">
            {/* Icon and line */}
            <div className="flex flex-col items-center mr-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: bgColor }}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" style={{ color: stepColor }} />
                ) : (
                  <Icon className="w-5 h-5" style={{ color: stepColor }} />
                )}
              </div>
              {!isLast && (
                <div 
                  className="w-0.5 h-8 my-1"
                  style={{ backgroundColor: isCompleted ? COLORS.green : D.border }}
                />
              )}
            </div>
            
            {/* Content */}
            <div className="pb-6">
              <p 
                className="font-medium text-sm"
                style={{ color: isCompleted || isCurrentStep ? COLORS.primary : COLORS.subtext }}
              >
                {isCompleted ? '✔ ' : ''}{step.label}
              </p>
              {isCurrentStep && (
                <p className="text-xs mt-1" style={{ color: COLORS.info }}>
                  Current step
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TransactionTimeline;
